/**
 * useProjectDetail — data fetching + derived state for the Project Detail
 * route (issue #1411). Extracted from `routes/projects/$projectId.tsx` so the
 * route file is composition-only; all GraphQL fetching, memoized derivations,
 * and the milestone/outcome-catalog delete flow live here.
 */
import { useCallback, useMemo, useState } from 'react';
import {
  useProject,
  useProjectMilestones,
  useProjectOutcomes,
  useProjectAssignments,
  useAllServices,
  useAllTeams,
  useTagCategories,
  useAllTags,
} from '../graphql/queries';
import {
  buildServiceNameMapFromResolution,
  buildServiceResolutionMap,
  buildServiceTeamIdMap,
  distinctTeamOptions,
  type ServiceResolutionMap,
  type TeamOption,
} from '../lib/serviceResolution';
import {
  buildMilestoneNameMap,
  buildOutcomeNameMap,
  EMPTY_ASSIGNED_SERVICES_FILTERS,
  filterAssignments,
  type AssignedServicesFilterState,
} from '../lib/assignedServicesFilter';
import { deleteProjectMilestone, deleteProjectOutcome, ApiError } from '../lib/apiClient';
import type {
  Project,
  ProjectMilestone,
  ProjectOutcome,
  ServiceAssignment,
} from '../graphql/types';
import type { Tag, TagCategory } from '../graphql/types/tag';
import {
  outcomeDistribution,
  statusByMilestone,
  statusByMilestoneAndTeam,
  countsByMilestoneAndOutcome,
  projectKpis,
  type OutcomeDistributionEntry,
  type MilestoneStatusCounts,
  type MilestoneAndTeamCounts,
  type MilestoneOutcomeCounts,
  type ProjectKpis,
} from '../lib/projectDashboard';

export interface UseProjectDetailResult {
  project: Project | null | undefined;
  projectLoading: boolean;
  projectError: Error | undefined;

  milestones: ProjectMilestone[];
  outcomes: ProjectOutcome[];
  allAssignments: ServiceAssignment[];
  assignments: Record<string, ServiceAssignment[]>;

  kpis: ProjectKpis;
  distribution: OutcomeDistributionEntry[];
  burnCounts: MilestoneStatusCounts[];
  burnTeamCounts: MilestoneAndTeamCounts;
  milestoneOutcomeCounts: MilestoneOutcomeCounts[];
  outcomeCountsByMilestoneId: Map<string, MilestoneOutcomeCounts>;

  serviceResolution: ServiceResolutionMap;
  teamOptions: TeamOption[];

  tagCategories: TagCategory[];
  allTags: Tag[];

  filteredAssignments: ServiceAssignment[];
  handleAssignedServicesFiltersChange: (filters: AssignedServicesFilterState) => void;

  milestoneInUse: (ms: ProjectMilestone) => boolean;
  outcomeInUse: (oc: ProjectOutcome) => boolean;

  refetchMilestones: () => void;
  refetchOutcomes: () => void;
  refetchAssignments: () => void;

  catalogDeleteError: string | null;
  isDeletingCatalog: boolean;
  requestDeleteMilestone: (milestone: ProjectMilestone) => void;
  requestDeleteOutcome: (outcome: ProjectOutcome) => void;
  deletingMilestone: ProjectMilestone | null;
  deletingOutcome: ProjectOutcome | null;
  cancelDeleteMilestone: () => void;
  cancelDeleteOutcome: () => void;
  confirmDeleteMilestone: () => Promise<void>;
  confirmDeleteOutcome: () => Promise<void>;
}

/** Data + derived state for the Project Detail page (issue #1411). */
export function useProjectDetail(projectId: string): UseProjectDetailResult {
  const { data: projectData, loading: projectLoading, error: projectError } = useProject(projectId);
  const { data: milestonesData, refetch: refetchMilestones } = useProjectMilestones(projectId);
  const { data: outcomesData, refetch: refetchOutcomes } = useProjectOutcomes(projectId);

  const project = projectData?.project;
  const milestones = milestonesData?.projectMilestones ?? [];
  const outcomes = outcomesData?.projectOutcomes ?? [];

  const milestoneIds = milestones.map((m) => m.entityId);
  const { assignments, refetch: refetchAssignments } = useProjectAssignments(milestoneIds);

  const allAssignments = Object.values(assignments).flat();
  const kpis = projectKpis(milestones, assignments);
  const distribution = outcomeDistribution(allAssignments, outcomes);
  const burnCounts = statusByMilestone(assignments, milestones);

  // Bulk-resolve names/teams for every assigned service (issue #495) — not
  // just services the viewer owns.
  const { data: allServicesData } = useAllServices();
  const allServices = allServicesData?.services?.edges?.map((e) => e.node) ?? [];
  const { data: allTeamsData } = useAllTeams();
  const allTeams = allTeamsData?.teams?.edges?.map((e) => e.node) ?? [];

  const serviceResolution = useMemo(
    () => buildServiceResolutionMap(allServices, allTeams),
    [allServices, allTeams]
  );
  const teamOptions = useMemo(() => distinctTeamOptions(serviceResolution), [serviceResolution]);

  // Assigned Services filter state, reported up from AssignedServicesTable
  // (issue #712) so Export can be scoped to exactly what the table currently
  // shows instead of every assignment in the project.
  const [assignedServicesFilters, setAssignedServicesFilters] =
    useState<AssignedServicesFilterState>(EMPTY_ASSIGNED_SERVICES_FILTERS);
  const handleAssignedServicesFiltersChange = useCallback(
    (filters: AssignedServicesFilterState) => setAssignedServicesFilters(filters),
    []
  );
  const filteredAssignments = useMemo(
    () =>
      filterAssignments(allAssignments, assignedServicesFilters, {
        serviceTeamIdMap: buildServiceTeamIdMap(serviceResolution),
        serviceNameMap: buildServiceNameMapFromResolution(serviceResolution),
        milestoneNameMap: buildMilestoneNameMap(milestones),
        outcomeNameMap: buildOutcomeNameMap(outcomes),
      }),
    [allAssignments, assignedServicesFilters, serviceResolution, milestones, outcomes]
  );

  // Tag/TagCategory catalog for the Export dialog's Three Rs Key / Hosting
  // Environment / user-selected tag-group columns (issue #647).
  const { data: tagCategoriesData } = useTagCategories();
  const tagCategories = tagCategoriesData?.tagCategories ?? [];
  const { tags: allTags } = useAllTags();

  // Team-segmented progress burn chart (issue #494) — reuses the same
  // serviceResolution map built above for #495, no duplicate fetch.
  const burnTeamCounts = useMemo(
    () => statusByMilestoneAndTeam(assignments, milestones, serviceResolution),
    [assignments, milestones, serviceResolution]
  );

  // Assigned-service counts by outcome, per milestone (issue #497) — derived
  // from the same assignments map already fetched for the dashboard/burn
  // chart, no extra round-trip.
  const milestoneOutcomeCounts = useMemo(
    () => countsByMilestoneAndOutcome(assignments, milestones, outcomes),
    [assignments, milestones, outcomes]
  );
  const outcomeCountsByMilestoneId = useMemo(
    () => new Map(milestoneOutcomeCounts.map((entry) => [entry.milestoneId, entry])),
    [milestoneOutcomeCounts]
  );

  // Edit/delete milestone & outcome catalog entries. Delete is gated on the
  // entry not being referenced by any service assignment.
  const [deletingMilestone, setDeletingMilestone] = useState<ProjectMilestone | null>(null);
  const [deletingOutcome, setDeletingOutcome] = useState<ProjectOutcome | null>(null);
  const [isDeletingCatalog, setIsDeletingCatalog] = useState(false);
  const [catalogDeleteError, setCatalogDeleteError] = useState<string | null>(null);

  const outcomeUsageCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of allAssignments) {
      counts.set(a.outcomeId, (counts.get(a.outcomeId) ?? 0) + 1);
    }
    return counts;
  }, [allAssignments]);

  const milestoneInUse = useCallback(
    (ms: ProjectMilestone): boolean => (assignments[ms.entityId]?.length ?? 0) > 0,
    [assignments]
  );
  const outcomeInUse = useCallback(
    (oc: ProjectOutcome): boolean => (outcomeUsageCounts.get(oc.entityId) ?? 0) > 0,
    [outcomeUsageCounts]
  );

  const requestDeleteMilestone = useCallback((milestone: ProjectMilestone) => {
    setCatalogDeleteError(null);
    setDeletingMilestone(milestone);
  }, []);
  const requestDeleteOutcome = useCallback((outcome: ProjectOutcome) => {
    setCatalogDeleteError(null);
    setDeletingOutcome(outcome);
  }, []);
  const cancelDeleteMilestone = useCallback(() => setDeletingMilestone(null), []);
  const cancelDeleteOutcome = useCallback(() => setDeletingOutcome(null), []);

  const confirmDeleteMilestone = useCallback(async (): Promise<void> => {
    if (!deletingMilestone) return;
    setIsDeletingCatalog(true);
    setCatalogDeleteError(null);
    try {
      await deleteProjectMilestone(deletingMilestone.entityId);
      setDeletingMilestone(null);
      void refetchMilestones();
      void refetchAssignments();
    } catch (err) {
      setCatalogDeleteError(err instanceof ApiError ? err.message : 'Failed to delete milestone');
      setDeletingMilestone(null);
    } finally {
      setIsDeletingCatalog(false);
    }
  }, [deletingMilestone, refetchMilestones, refetchAssignments]);

  const confirmDeleteOutcome = useCallback(async (): Promise<void> => {
    if (!deletingOutcome) return;
    setIsDeletingCatalog(true);
    setCatalogDeleteError(null);
    try {
      await deleteProjectOutcome(deletingOutcome.entityId);
      setDeletingOutcome(null);
      void refetchOutcomes();
    } catch (err) {
      setCatalogDeleteError(err instanceof ApiError ? err.message : 'Failed to delete outcome');
      setDeletingOutcome(null);
    } finally {
      setIsDeletingCatalog(false);
    }
  }, [deletingOutcome, refetchOutcomes]);

  return {
    project,
    projectLoading,
    projectError,
    milestones,
    outcomes,
    allAssignments,
    assignments,
    kpis,
    distribution,
    burnCounts,
    burnTeamCounts,
    milestoneOutcomeCounts,
    outcomeCountsByMilestoneId,
    serviceResolution,
    teamOptions,
    tagCategories,
    allTags,
    filteredAssignments,
    handleAssignedServicesFiltersChange,
    milestoneInUse,
    outcomeInUse,
    refetchMilestones,
    refetchOutcomes,
    refetchAssignments,
    catalogDeleteError,
    isDeletingCatalog,
    requestDeleteMilestone,
    requestDeleteOutcome,
    deletingMilestone,
    deletingOutcome,
    cancelDeleteMilestone,
    cancelDeleteOutcome,
    confirmDeleteMilestone,
    confirmDeleteOutcome,
  };
}
